import { useParams, Link } from 'react-router';
import { useChannelsStore } from '@/store/useChannelsStore';
import { usePlayerStore } from '@/store/usePlayerStore';
import { ChannelGrid } from '@/components/channel/ChannelGrid';
import { PageHeader } from '@/components/common/PageHeader';
import { ChannelGridSkeleton } from '@/components/common/Skeleton';
import { countryCodeToFlag, sortChannelsByRelevance } from '@/lib/channels';

export function CountryPage() {
  const { code = '' } = useParams<{ code: string }>();
  const codeUpper = code.toUpperCase();
  const sportsChannels = useChannelsStore((s) => s.sportsChannels);
  const countries = useChannelsStore((s) => s.countries);
  const isLoading = useChannelsStore((s) => s.isLoadingInitial);
  const setCurrentChannel = usePlayerStore((s) => s.setCurrentChannel);
  const currentChannel = usePlayerStore((s) => s.currentChannel);

  const country = countries.find((c) => c.code === codeUpper);
  const flag = country?.flag || countryCodeToFlag(codeUpper);
  const countryName = country?.name || codeUpper;

  if (isLoading) {
    return <ChannelGridSkeleton count={8} />;
  }

  const filtered = sortChannelsByRelevance(
    sportsChannels.filter((c) => c.countryCode === codeUpper)
  );

  return (
    <div className="flex flex-col gap-6 h-full">
      <PageHeader
        backTo="/"
        icon={<span aria-hidden="true">{flag}</span>}
        title={countryName}
        subtitle={
          <>
            {filtered.length === 1
              ? '1 sports channel'
              : `${filtered.length.toLocaleString()} sports channels`}
            {country && (
              <>
                {' · '}
                <span className="text-text-muted">
                  {country.count.toLocaleString()} total in dataset
                </span>
              </>
            )}
          </>
        }
        action={
          <span className="text-xs font-mono text-text-muted bg-bg-elevated border border-border px-2 py-1 rounded">
            {codeUpper}
          </span>
        }
      />

      <div className="flex-1 min-h-0">
        <ChannelGrid
          channels={filtered}
          onChannelClick={setCurrentChannel}
          activeChannelId={currentChannel?.id}
          columns={4}
          emptyTitle={`No sports channels from ${countryName}`}
          emptyDescription="This country may not have sports channels in the iptv-org dataset."
        />
      </div>

      {country === undefined && (
        <div className="text-center pt-2">
          <Link to="/" className="text-sm text-accent hover:underline">
            ← Back to home
          </Link>
        </div>
      )}
    </div>
  );
}
